import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { TModalStore, TPositionMetricEntity, useMst } from '@trader/store';
import { TradingIsClosed, ModalTitle, Progress } from '@trader/components';
import { TradingContext } from '@trader/contexts';
import { calculateSpread } from '@trader/utils';
import { useI18next } from '@trader/services';
import { useBusinessLogic } from '@trader/hooks';

import {
  IPositionMetricStore,
  TradingModalHeader,
} from '../components/tradingModalHeader';
import { BuyButton } from '../components/buyButton';
import { OrderInput } from '../components/orderInput';
import { SuccessChangeOrder } from '../components/successChangeOrder';
import { ConfirmButton } from '../components/confirmButton';

import * as Styled from './styled';
import { IInstrumentDetailsBE, IOrderDetailsBE } from '@trader/api';

const calculatePercent = 100;

export const ClosePosition: React.FC = observer(() => {
  const store = useMst();
  const { translate } = useI18next();
  const { instrument, clearPreviousState, handleStartTrading, realTimePrice } =
    useContext(TradingContext);

  const { applyCondition: applyConditionThird } =
    useBusinessLogic('third_case');

  const [quantityToClose, setQuantityToClose] = useState('');
  const [quantityError, setQuantityError] = useState('');

  const modal: TModalStore = store.ui.modal;
  const placedOrder = store.pages.trading.placedOrder;

  const positionId = modal.options.get('positionId');
  const symbol = modal.options.get('symbol');
  const type = modal.options.get('type');

  const metric =
    store.entities.positionsMetrics.get<TPositionMetricEntity>(positionId);

  const getInstrumentDetails = async () => {
    if (symbol) {
      const [detailsResult, orderDetailsResult] =
        (await store.pages.trading.getAllInstrumentDetailsAsync.run(
          symbol
        )) as [IInstrumentDetailsBE, IOrderDetailsBE];

      handleStartTrading(
        {
          ...detailsResult,
          ...orderDetailsResult,
        },
        type
      );
    }
  };

  const handleClosePosition = () => {
    metric?.closePositionAsync.run({
      positionId,
      quantity: Number(quantityToClose),
    });
  };

  useEffect(() => {
    getInstrumentDetails();
    return () => {
      clearPreviousState();
    };
  }, [symbol]);

  useEffect(() => {
    return () => {
      setQuantityToClose('');
      setQuantityError('');
    };
  }, []);

  useEffect(() => {
    if (metric) {
      const isMultiple =
        +quantityToClose &&
        metric.quantity % 1 === 0 &&
        (+quantityToClose * calculatePercent) %
          (instrument.general.step * calculatePercent);
      const isHigher = +quantityToClose > metric.quantity;
      const isLess = +quantityToClose < instrument.market.minAmountValue;

      setQuantityError('');
      isMultiple &&
        setQuantityError(
          `Quantity should be a multiple of the minimum lots increment ${instrument.general.step})`
        );
      isHigher &&
        setQuantityError(() =>
          instrument.general.step === metric.quantity
            ? `Amount must be ${metric.quantity}`
            : `Amount must be less or equal than ${metric.quantity}`
        );
      isLess &&
        setQuantityError(
          `Quantity should be a multiple of the minimum lots increment ${instrument.general.step}`
        );
    }
  }, [quantityToClose, metric, instrument]);

  useEffect(() => {
    metric?.quantity && setQuantityToClose(metric.quantity.toString());
  }, [metric?.quantity]);

  if (!instrument.general.symbol) {
    return (
      <Styled.Root>
        <Progress />
      </Styled.Root>
    );
  }

  if (applyConditionThird().status === 'failed') {
    return (
      <Styled.Root>
        <TradingIsClosed
          iconTypeName='warning'
          title={translate('WARNINGS.TRADING_IS_DISABLED')}
          content={translate('WARNINGS.PLEASE_CONTACT_SUPPORT', {
            email: import.meta.env.VITE_SUPPORT_EMAIL,
          })}
          buttonContent={translate('COMMON.LABELS.PROCEED')}
          onClick={applyConditionThird()?.action}
        />
      </Styled.Root>
    );
  }

  return (
    <Styled.Root>
      {!placedOrder ? (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <React.Fragment>
          <ModalTitle title={translate('COMMON.LABELS.CLOSE_POSITION')} />
          {instrument.general.isOpenTrading ? (
            <Styled.Position>
              <TradingModalHeader<IPositionMetricStore>
                type='position'
                metric={metric}
              />
              <Styled.ActionButtons>
                <BuyButton isDisabled order='Market' isAsk type='Sell' />
                <Styled.Spread>
                  {calculateSpread({
                    ask: Number(realTimePrice.ask),
                    bid: Number(realTimePrice.bid),
                    pipSize: instrument.general.pipSize,
                  })}
                </Styled.Spread>
                <BuyButton isDisabled order='Market' type='Buy' />
              </Styled.ActionButtons>
              <Styled.Main>
                <OrderInput
                  disabled={false}
                  placeholder={translate('COMMON.LABELS.QUANTITY')}
                  maxValue={metric?.quantity || 0}
                  minValue={instrument.market.minAmountValue}
                  fixDigitAfterDot={instrument.general.fixDigitAfterDot}
                  step={instrument.general.step}
                  shouldCheckActivityOfButtons
                  value={quantityToClose}
                  onChangeInput={value => {
                    setQuantityToClose(value);
                  }}
                  errorMsg={quantityError}
                />
              </Styled.Main>
              <ConfirmButton
                isDisabled={
                  metric?.closePositionAsync.inProgress || !!quantityError
                }
                isLoading={metric?.closePositionAsync.inProgress}
                onClick={handleClosePosition}
                title={translate('COMMON.LABELS.CLOSE_POSITION')}
              />
            </Styled.Position>
          ) : (
            <TradingIsClosed
              iconTypeName='warning'
              title={translate('WARNINGS.CURRENTLY_OUT_OF_TRADING_HOURS')}
            />
          )}
        </React.Fragment>
      ) : (
        <SuccessChangeOrder type='market' isClose />
      )}
    </Styled.Root>
  );
});
